import React, { useState, useEffect } from 'react';
import { FlaskConical, Rocket, Zap } from 'lucide-react';
import { MainView } from '../components/dropgen/MainView';
import { useDropGenSocket } from '../hooks/useDropGenSocket';
import { useDropGenStore } from '../stores/dropgenStore';
import { UserMenu } from '../components/auth/UserMenu';
import { checkAutoStoreHealth, getImageProcessorStatus } from '../components/dropgen/api';

export default function DropGenPage() {
  const { wsConnected, activeView, setActiveView, currentResearch } = useDropGenStore();
  const [serviceHealth, setServiceHealth] = useState<{ database: string; ollama: string }>({ database: 'unknown', ollama: 'unknown' });
  const [imageProcessorAvailable, setImageProcessorAvailable] = useState(false);

  useDropGenSocket();

  // Fetch service health
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const health = await checkAutoStoreHealth();
        setServiceHealth({ database: health.services?.database || 'unknown', ollama: health.services?.ollama || 'unknown' });
      } catch (e) { /* optional */ }
      try {
        const imgStatus = await getImageProcessorStatus();
        setImageProcessorAvailable(imgStatus.available || false);
      } catch (e) { /* optional */ }
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 10000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    {
      id: 'researchlab' as const,
      label: 'Research',
      icon: FlaskConical,
      gradient: 'from-cyan-500 to-blue-500',
      shadow: 'shadow-cyan-500/25'
    },
    {
      id: 'autostore' as const,
      label: 'AutoStore',
      icon: Rocket,
      gradient: 'from-purple-500 to-pink-500',
      shadow: 'shadow-purple-500/25'
    },
  ];

  const isResearching = currentResearch?.status === 'researching';

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-[#0a0a0b]">
      {/* Sticky Header */}
      <header className="shrink-0 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/[0.06] bg-[#0a0a0b]/80 backdrop-blur-xl">
        <div className="relative flex items-center justify-center">
          {/* Left: Logo */}
          <div className="absolute left-0 flex items-center gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            {/* Research Status - next to logo on desktop */}
            {currentResearch && activeView === 'researchlab' && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white/[0.03] rounded-lg border border-white/[0.06]">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  currentResearch.status === 'completed' ? 'bg-green-400 shadow-sm shadow-green-400/50' :
                  currentResearch.status === 'researching' ? 'bg-cyan-400 animate-pulse shadow-sm shadow-cyan-400/50' :
                  currentResearch.status === 'error' ? 'bg-red-400' :
                  'bg-gray-500'
                }`} />
                <span className="text-xs text-gray-400 font-medium truncate max-w-[150px]">
                  {currentResearch.niche}
                </span>
                {currentResearch.status === 'researching' && currentResearch.progress > 0 && (
                  <span className="text-xs text-cyan-400 font-semibold">
                    {currentResearch.progress}%
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Center: Nav Switcher - Glowing Segments */}
          <div className="inline-flex p-1 bg-white/[0.03] rounded-xl border border-white/[0.06]">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              const showPulse = item.id === 'researchlab' && isResearching;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id)}
                  className={`relative px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-r ${item.gradient} ${item.shadow} text-white shadow-lg`
                      : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]'
                  }`}
                >
                  <Icon className={`w-4 h-4 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                  <span className="hidden sm:inline">{item.label}</span>
                  {showPulse && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-cyan-400 rounded-full animate-pulse ring-2 ring-[#0a0a0b]" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: Service Status + User */}
          <div className="absolute right-0 flex items-center gap-2 sm:gap-3">
            {/* Service Status Indicators - DB/AI/Img/Live */}
            <div className="flex items-center gap-2 px-2 py-1 sm:px-2.5 sm:py-1.5 bg-white/[0.03] rounded-lg border border-white/[0.06]">
              <div className="flex items-center gap-1" title="Database">
                <span className={`w-1.5 h-1.5 rounded-full ${serviceHealth.database === 'healthy' ? 'bg-green-400 shadow-sm shadow-green-400/50' : 'bg-red-400'}`} />
                <span className="text-[10px] text-gray-500">DB</span>
              </div>
              <div className="w-px h-3 bg-white/10" />
              <div className="flex items-center gap-1" title="Ollama AI">
                <span className={`w-1.5 h-1.5 rounded-full ${serviceHealth.ollama === 'healthy' ? 'bg-green-400 shadow-sm shadow-green-400/50' : 'bg-yellow-400'}`} />
                <span className="text-[10px] text-gray-500">AI</span>
              </div>
              <div className="w-px h-3 bg-white/10" />
              <div className="flex items-center gap-1" title="Image Processor">
                <span className={`w-1.5 h-1.5 rounded-full ${imageProcessorAvailable ? 'bg-green-400 shadow-sm shadow-green-400/50' : 'bg-red-400'}`} />
                <span className="text-[10px] text-gray-500">Img</span>
              </div>
              <div className="w-px h-3 bg-white/10" />
              <div className="flex items-center gap-1" title="WebSocket">
                <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-400 shadow-sm shadow-green-400/50 animate-pulse' : 'bg-red-400'}`} />
                <span className="text-[10px] text-gray-500">Live</span>
              </div>
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Content Area - fills remaining space */}
      <main className="flex-1 min-h-0 overflow-hidden">
        <MainView />
      </main>
    </div>
  );
}
